import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import { bidderAPI } from '../../../utils/api';
import { FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import DataTable from '../../../components_shared/DataTable';
import TableToolbar from '../../../components_shared/TableToolbar';

function BiddersTable({ onEdit, onCreate, reloadKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await bidderAPI.getAll();
      setRows(Array.isArray(res.data) ? res.data : (res.data.content || []));
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [reloadKey]);

  const remove = async (id) => {
    const ok = await Swal.fire({ title: 'Remove Bidder?', text: 'This will remove the bidder from the current tender.', icon: 'warning', showCancelButton: true });
    if (ok.isConfirmed) {
      try {
        await bidderAPI.delete(id);
        load();
        Swal.fire({ title: 'Deleted', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
      } catch (err) { Swal.fire('Error', err.message, 'error'); }
    }
  };

  const validate = async id => { await bidderAPI.validate(id); await load(); };
  const disqualify = async id => { await bidderAPI.disqualify(id); await load(); };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { 
      accessorKey: 'publisher_name', 
      header: 'Publisher / Bidder', 
      cell: ({ getValue }) => <span style={{ fontWeight: 600, color: '#2d3436' }}>{getValue() || '-'}</span> 
    },
    { 
      accessorKey: 'score', 
      header: 'Tech. Score', 
      cell: ({ getValue }) => <strong style={{ color: '#0f6fad' }}>{getValue() || 'N/A'}</strong> 
    },
    { 
      accessorKey: 'status', 
      header: 'Qualification',
      cell: ({ getValue }) => (
        <span className={`eduss-global-status-badge ${getValue()?.toLowerCase()}`}>
          {getValue()}
        </span>
      )
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn edit" onClick={() => onEdit(row.original)}><FaEdit /></button>
          <button className="eduss-global-action-btn delete" onClick={() => remove(row.original.id)}><FaTrash /></button>
          {row.original.status === 'DISQUALIFIED' && (
            <button className="eduss-global-action-btn active" style={{ background: 'rgba(16, 172, 132, 0.1)', color: '#10ac84' }} onClick={() => validate(row.original.id)} title="Re-qualify"><FaCheck /></button>
          )}
          {row.original.status === 'QUALIFIED' && (
            <button className="eduss-global-action-btn delete" onClick={() => disqualify(row.original.id)} title="Disqualify"><FaTimes /></button>
          )}
        </div>
      ),
    },
  ];

  const filteredData = rows.filter(r => 
    !filter || r.publisher_name?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card" style={{ padding: '0 0 30px 0' }}>
      <TableToolbar 
        title="Qualified Bidders"
        subtitle="Technical evaluation and qualification status for active tenders"
        onAdd={onCreate}
        onSearch={setFilter}
      />

      <div style={{ padding: '0 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
          enableSelection={true}
        />
      </div>
    </div>
  );
}

BiddersTable.propTypes = { onEdit: PropTypes.func, onCreate: PropTypes.func, reloadKey: PropTypes.number };
export default BiddersTable;
